import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { 
  Building2, 
  ShieldCheck, 
  ArrowRight, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  Lock, 
  Mail, 
  User 
} from 'lucide-react';

interface InviteData {
  id: string;
  name: string;
  invite_email: string;
  role: string;
  status: string;
  workspace_owner_id: string;
  owner_business_name?: string;
  owner_name?: string;
}

export default function InviteAcceptPage() {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    async function loadInvite() {
      if (!token) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        // 1. Consulta segura via RPC com SECURITY DEFINER (bypassa RLS e busca dados do proprietário)
        const { data: rpcData, error: rpcError } = await supabase.rpc('get_team_invite_details', {
          p_token: token
        });

        if (rpcData && !rpcError) {
          if (!isCancelled) {
            setInviteData(rpcData as InviteData);
            setLoading(false);
          }
          return;
        }

        // Fallback: consulta direta caso a migração ainda não tenha sido executada
        const { data, error } = await supabase
          .from('workspace_members')
          .select(`
            id,
            name,
            invite_email,
            role,
            status,
            workspace_owner_id
          `)
          .eq('invite_token', token)
          .maybeSingle();

        if (error || !data) {
          if (!isCancelled) {
            setNotFound(true);
            setLoading(false);
          }
          return;
        }

        // Buscar dados da empresa do Master
        let ownerBusiness = 'Empresa Parceira';
        let ownerName = 'Gestor';
        try {
          const { data: ownerProfile } = await supabase
            .from('users')
            .select('business_name, name')
            .eq('id', data.workspace_owner_id)
            .maybeSingle();

          if (ownerProfile) {
            ownerBusiness = ownerProfile.business_name || ownerBusiness;
            ownerName = ownerProfile.name || ownerName;
          }
        } catch (e) {
          console.warn('Erro ao carregar dados do proprietário:', e);
        }

        if (!isCancelled) {
          setInviteData({
            ...data,
            owner_business_name: ownerBusiness,
            owner_name: ownerName
          });
          setLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Erro ao verificar token de convite:', err);
          setNotFound(true);
          setLoading(false);
        }
      }
    }

    loadInvite();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteData) return;

    if (password.length < 6) {
      setErrorMessage('A senha deve conter no mínimo 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('As senhas digitadas não coincidem.');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      // 1. Criar a conta do colaborador no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: inviteData.invite_email,
        password,
        options: {
          data: {
            name: inviteData.name,
            is_team_member: true,
            workspace_owner_id: inviteData.workspace_owner_id
          }
        }
      });

      let authenticatedUserId = authData?.user?.id;

      // Se o e-mail já possuir conta (user_already_exists), faz login direto
      if (authError && (authError.message.includes('already registered') || (authError as any).status === 422)) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: inviteData.invite_email,
          password
        });

        if (signInError) {
          throw new Error('Este e-mail já possui uma conta no R-MAX. A senha digitada não confere.');
        }
        authenticatedUserId = signInData.user?.id;
      } else if (authError) {
        throw authError;
      }

      // 2. Vincular o membro ao workspace do Master e ativar status atomicamente
      if (authenticatedUserId && token) {
        const { data: rpcResult, error: rpcError } = await supabase.rpc('accept_team_invite', {
          p_token: token,
          p_user_id: authenticatedUserId
        });

        if (rpcError || !rpcResult?.success) {
          // Fallback para update direto
          const { error: updateError } = await supabase
            .from('workspace_members')
            .update({
              member_user_id: authenticatedUserId,
              status: 'active',
              updated_at: new Date().toISOString()
            })
            .eq('id', inviteData.id);

          if (updateError) {
            console.error('Erro ao atualizar membro:', updateError);
          }
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/app');
      }, 2000);
    } catch (err: any) {
      setErrorMessage(err.message || 'Falha ao aceitar convite.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center text-zinc-600 p-4 space-y-4 font-sans">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
        <div className="text-center">
          <h2 className="text-base font-black text-zinc-900">Validando Convite...</h2>
          <p className="text-xs text-zinc-500 mt-1">Conectando aos servidores do R-MAX</p>
        </div>
      </div>
    );
  }

  if (notFound || !inviteData) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center text-zinc-800 p-4 font-sans">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-3xl p-6 text-center space-y-4 shadow-xl">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 mx-auto flex items-center justify-center border border-rose-100">
            <AlertCircle className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-lg font-black text-zinc-900">Convite Não Encontrado</h2>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Este link de convite é inválido, expirou ou já foi utilizado. Solicite um novo link ao gestor da empresa pelo WhatsApp.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-black text-xs rounded-xl border border-zinc-200 transition-all cursor-pointer"
          >
            Voltar ao Início
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center p-4 font-sans relative overflow-hidden">
      {/* Glow de Fundo */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-md w-full bg-white border border-zinc-100 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl relative z-10">
        
        {/* Cabeçalho */}
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-700 flex items-center justify-center mx-auto shadow-sm">
            <Building2 className="w-6 h-6" />
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-800 px-3 py-1 rounded-full border border-emerald-200">
            Convite de Equipe
          </span>
          <h2 className="text-xl font-black text-zinc-900 pt-1">
            {inviteData.owner_business_name}
          </h2>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Você foi convidado por <span className="font-bold text-zinc-700">{inviteData.owner_name}</span> para acessar o R-MAX com a função de <span className="font-bold text-brand capitalize">{inviteData.role}</span>.
          </p>
        </div>

        {success ? (
          <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-3 animate-in zoom-in-95">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto animate-bounce" />
            <h3 className="text-sm font-black text-emerald-900">Acesso Criado com Sucesso!</h3>
            <p className="text-xs text-emerald-800">
              Redirecionando você diretamente para o painel de trabalho da sua empresa...
            </p>
          </div>
        ) : (
          <form onSubmit={handleAcceptInvite} className="space-y-4">
            {errorMessage && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold rounded-xl flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-zinc-700">Seu Nome</label>
              <div className="relative">
                <User className="absolute left-3.5 top-3 h-4 w-4 text-zinc-400" />
                <input
                  type="text"
                  readOnly
                  value={inviteData.name}
                  className="w-full bg-zinc-100/80 border border-zinc-200 pl-10 pr-4 py-2.5 rounded-xl text-xs font-bold text-zinc-700 outline-none cursor-not-allowed"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-zinc-700">Seu E-mail Profissional</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-3 h-4 w-4 text-zinc-400" />
                <input
                  type="email"
                  readOnly
                  value={inviteData.invite_email}
                  className="w-full bg-zinc-100/80 border border-zinc-200 pl-10 pr-4 py-2.5 rounded-xl text-xs font-bold text-zinc-700 outline-none cursor-not-allowed"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black text-zinc-900">Crie sua Senha de Acesso</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3 h-4 w-4 text-zinc-400" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mínimo de 6 caracteres"
                  required
                  className="w-full bg-zinc-50 border border-zinc-200 focus:border-brand pl-10 pr-4 py-2.5 rounded-xl text-xs outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-black text-zinc-900">Confirme sua Senha</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-3 h-4 w-4 text-zinc-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repita sua senha"
                  required
                  className="w-full bg-zinc-50 border border-zinc-200 focus:border-brand pl-10 pr-4 py-2.5 rounded-xl text-xs outline-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-brand text-white font-black text-xs rounded-xl shadow-lg hover:bg-brand-dark transition-all cursor-pointer flex items-center justify-center gap-2 mt-2 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Configurando seu acesso...</span>
                </>
              ) : (
                <>
                  <span>Ativar Meu Acesso & Entrar</span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        )}

        <div className="pt-2 text-center">
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-400">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>Ambiente seguro corporativo com criptografia SHA-256</span>
          </div>
        </div>

      </div>
    </div>
  );
}
